import { MAX_EVENT_TITLE } from "constants/productLimits";

export const MAX_COUNTDOWN_EVENT_COUNT = 3;

export const COUNTDOWN_NUMBER_POSITIONS = {
  LEFT: "left",
  RIGHT: "right",
};

export const COUNTDOWN_CELL_BORDER_STYLES = {
  SUBTLE: "subtle",
  ACCENT: "accent",
};

const clampEventCount = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;
  return Math.min(MAX_COUNTDOWN_EVENT_COUNT, Math.max(1, Math.round(parsed)));
};

const normalizeText = (value) => (typeof value === "string" ? value.trim() : "");
const normalizeEditableText = (value) =>
  (typeof value === "string" ? value.slice(0, MAX_EVENT_TITLE) : "");

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const normalizeManualSlot = (slot = {}) => ({
  eventName: normalizeEditableText(slot.eventName),
  eventDate: normalizeText(slot.eventDate),
});

export const normalizeCountdownEventCount = (contentData = {}) =>
  clampEventCount(contentData?.eventCount);

export const normalizeCountdownNumberPosition = (value) =>
  value === COUNTDOWN_NUMBER_POSITIONS.RIGHT
    ? COUNTDOWN_NUMBER_POSITIONS.RIGHT
    : COUNTDOWN_NUMBER_POSITIONS.LEFT;

export const normalizeCountdownCellBorderStyle = (value) =>
  value === COUNTDOWN_CELL_BORDER_STYLES.ACCENT
    ? COUNTDOWN_CELL_BORDER_STYLES.ACCENT
    : COUNTDOWN_CELL_BORDER_STYLES.SUBTLE;

export const normalizeManualCountdownEvents = (contentData = {}) => {
  const manualEvents = normalizeArray(contentData.manualEvents);
  return Array.from({ length: MAX_COUNTDOWN_EVENT_COUNT }, (_, index) => {
    const storedSlot = normalizeManualSlot(manualEvents[index]);
    if (index === 0) {
      return {
        eventName: normalizeEditableText(contentData.eventName) || storedSlot.eventName,
        eventDate: normalizeText(contentData.eventDate) || storedSlot.eventDate,
      };
    }
    return storedSlot;
  });
};

export const normalizeTeacherHubCountdownEventIds = (contentData = {}) => {
  const teacherHubEventIds = normalizeArray(contentData.teacherHubEventIds);
  return Array.from({ length: MAX_COUNTDOWN_EVENT_COUNT }, (_, index) => {
    if (index === 0) {
      return normalizeText(contentData.teacherHubEventId) || normalizeText(teacherHubEventIds[index]);
    }
    return normalizeText(teacherHubEventIds[index]);
  });
};

export const normalizeTeacherHubCountdownDisplayLabels = (contentData = {}) => {
  const rawLabels = normalizeArray(contentData.teacherHubDisplayLabels);
  return Array.from({ length: MAX_COUNTDOWN_EVENT_COUNT }, (_, index) => {
    const rawLabel = rawLabels[index];
    if (typeof rawLabel === "string") {
      return normalizeEditableText(rawLabel);
    }
    return normalizeEditableText(rawLabel?.label);
  });
};

export const normalizeCountdownEventSettings = (contentData = {}) => {
  const eventCount = normalizeCountdownEventCount(contentData);
  return {
    eventCount,
    numberPosition: normalizeCountdownNumberPosition(contentData.numberPosition),
    eventCellBorderStyle: normalizeCountdownCellBorderStyle(contentData.eventCellBorderStyle),
    manualEvents: normalizeManualCountdownEvents(contentData),
    teacherHubEventIds: normalizeTeacherHubCountdownEventIds(contentData),
    teacherHubDisplayLabels: normalizeTeacherHubCountdownDisplayLabels(contentData),
  };
};

const moveArrayItem = (items = [], fromIndex, toIndex) => {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);
  return nextItems;
};

export const reorderCountdownEventSlots = (contentData = {}, fromIndex, toIndex) => {
  const settings = normalizeCountdownEventSettings(contentData);
  const eventCount = settings.eventCount;
  const canReorder =
    Number.isInteger(fromIndex)
    && Number.isInteger(toIndex)
    && fromIndex !== toIndex
    && fromIndex >= 0
    && toIndex >= 0
    && fromIndex < eventCount
    && toIndex < eventCount;

  const manualEvents = canReorder
    ? moveArrayItem(settings.manualEvents, fromIndex, toIndex)
    : settings.manualEvents;
  const teacherHubEventIds = canReorder
    ? moveArrayItem(settings.teacherHubEventIds, fromIndex, toIndex)
    : settings.teacherHubEventIds;
  const teacherHubDisplayLabels = canReorder
    ? moveArrayItem(settings.teacherHubDisplayLabels, fromIndex, toIndex)
    : settings.teacherHubDisplayLabels;

  return {
    ...contentData,
    eventCount: settings.eventCount,
    numberPosition: settings.numberPosition,
    eventCellBorderStyle: settings.eventCellBorderStyle,
    manualEvents,
    teacherHubEventIds,
    teacherHubDisplayLabels,
    eventName: manualEvents[0]?.eventName || "",
    eventDate: manualEvents[0]?.eventDate || "",
    teacherHubEventId: teacherHubEventIds[0] || "",
  };
};

export const deleteCountdownEventSlot = (contentData = {}, index) => {
  const settings = normalizeCountdownEventSettings(contentData);
  const canDelete =
    settings.eventCount > 1
    && Number.isInteger(index)
    && index >= 0
    && index < settings.eventCount;

  if (!canDelete) {
    return {
      ...contentData,
      ...settings,
      eventName: settings.manualEvents[0]?.eventName || "",
      eventDate: settings.manualEvents[0]?.eventDate || "",
      teacherHubEventId: settings.teacherHubEventIds[0] || "",
    };
  }

  const manualEvents = [...settings.manualEvents];
  const teacherHubEventIds = [...settings.teacherHubEventIds];
  const teacherHubDisplayLabels = [...settings.teacherHubDisplayLabels];
  manualEvents.splice(index, 1);
  teacherHubEventIds.splice(index, 1);
  teacherHubDisplayLabels.splice(index, 1);
  manualEvents.push({ eventName: "", eventDate: "" });
  teacherHubEventIds.push("");
  teacherHubDisplayLabels.push("");

  return {
    ...contentData,
    eventCount: settings.eventCount - 1,
    numberPosition: settings.numberPosition,
    eventCellBorderStyle: settings.eventCellBorderStyle,
    manualEvents,
    teacherHubEventIds,
    teacherHubDisplayLabels,
    eventName: manualEvents[0]?.eventName || "",
    eventDate: manualEvents[0]?.eventDate || "",
    teacherHubEventId: teacherHubEventIds[0] || "",
  };
};

export const buildManualCountdownTargets = (contentData = {}) => {
  const settings = normalizeCountdownEventSettings(contentData);
  return settings.manualEvents
    .slice(0, settings.eventCount)
    .map((slot, index) => ({
      eventName: normalizeText(slot.eventName),
      eventDate: slot.eventDate,
      source: "manual",
      resolvedEventId: "",
      slotIndex: index,
    }))
    .filter((target) => target.eventName && target.eventDate);
};

export const buildCountdownContentToSave = (draft = {}, previousContent = {}) => {
  const settings = normalizeCountdownEventSettings(draft);
  const manualEvents = settings.manualEvents;
  const teacherHubEventIds = settings.teacherHubEventIds;
  const teacherHubDisplayLabels = settings.teacherHubDisplayLabels;

  return {
    ...previousContent,
    ...draft,
    eventCount: settings.eventCount,
    numberPosition: settings.numberPosition,
    eventCellBorderStyle: settings.eventCellBorderStyle,
    eventName: manualEvents[0]?.eventName || draft.eventName || "",
    eventDate: manualEvents[0]?.eventDate || draft.eventDate || "",
    teacherHubEventId: teacherHubEventIds[0] || draft.teacherHubEventId || "",
    manualEvents,
    teacherHubEventIds,
    teacherHubDisplayLabels,
  };
};
